import {BasicObject} from '@/types';
import {envConfig} from './env.config';
import {isAppLikeExperience} from './interface';

export const H5_MIGRATION_INVITATION_APPLY_ROUTE = 'InvitationApply';
export const H5_MIGRATION_INVITATION_APPLY_PATH = '/invitation-apply';
/** 与 main-navigation Chat Tab 的 link 一致，用于 H5 迁移下进入完整壳层打开客服页 */
export const H5_MIGRATION_CHAT_ROUTE = 'Chat';
export const H5_MIGRATION_CHAT_PATH = '/index/chat';

/** 仅正式生产 Web 走迁移重定向；UAT / 本地 / 内部预览域名均正常使用站内应用 */
const H5_MIGRATION_ENABLED_ENVS = new Set(['prod']);
const H5_MIGRATION_ALLOWED_ROUTES = new Set([
  H5_MIGRATION_INVITATION_APPLY_ROUTE,
  H5_MIGRATION_CHAT_ROUTE,
]);

export type H5MigrationNavigationTarget =
  | {routeName: string; params?: BasicObject}
  | {externalUrl: string};

function readH5MigrationGuideUrlFromEnv(): string | undefined {
  try {
    const v =
      typeof process !== 'undefined' &&
      process.env &&
      process.env.REACT_APP_H5_MIGRATION_GUIDE_URL;
    return typeof v === 'string' && v.trim() ? v.trim() : undefined;
  } catch {
    return undefined;
  }
}

/**
 * 未配置环境变量时的默认 H5 下载引导外链。
 * 可通过 REACT_APP_H5_MIGRATION_GUIDE_URL 覆盖（https 绝对地址）。
 */
export const H5_MIGRATION_GUIDE_DEFAULT_EXTERNAL_URL =
  'https://www.rupiibet.io/';

/**
 * H5 迁移时下载引导的完整 URL（外链）。
 * 优先 REACT_APP_H5_MIGRATION_GUIDE_URL，否则使用 {@link H5_MIGRATION_GUIDE_DEFAULT_EXTERNAL_URL}。
 */
export function getH5MigrationExternalGuideUrl(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  const configured = readH5MigrationGuideUrlFromEnv();
  if (configured) {
    return configured;
  }
  return H5_MIGRATION_GUIDE_DEFAULT_EXTERNAL_URL;
}
const H5_MIGRATION_APP_ENTRY_PATHS = new Set([
  H5_MIGRATION_INVITATION_APPLY_PATH,
  H5_MIGRATION_CHAT_PATH,
]);

export type H5MigrationMode = 'disabled' | 'guide' | 'app';

function shouldBypassH5MigrationForHost(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const host = window.location.hostname.toLowerCase();
  if (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '[::1]' ||
    host.endsWith('.local')
  ) {
    return true;
  }
  // 连接生产 API 的内部预览域名，非对外生产站点
  if (host.includes('-pre.')) {
    return true;
  }
  return false;
}

export function isH5MigrationEnabled() {
  return (
    H5_MIGRATION_ENABLED_ENVS.has(envConfig.environment) &&
    !isAppLikeExperience() &&
    !shouldBypassH5MigrationForHost()
  );
}

export function normalizeH5MigrationPath(pathname?: string | null) {
  if (!pathname) {
    return '/';
  }
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
}

export function getH5MigrationMode(pathname?: string | null): H5MigrationMode {
  if (!isH5MigrationEnabled()) {
    return 'disabled';
  }

  const currentPath = normalizeH5MigrationPath(pathname);
  if (H5_MIGRATION_APP_ENTRY_PATHS.has(currentPath)) {
    return 'app';
  }

  return 'guide';
}

export function shouldRedirectForH5Migration(routeName?: string | null) {
  return (
    isH5MigrationEnabled() &&
    !!routeName &&
    !H5_MIGRATION_ALLOWED_ROUTES.has(routeName)
  );
}

export function getH5MigrationNavigationTarget(
  routeName: string,
  params?: BasicObject,
): H5MigrationNavigationTarget {
  if (shouldRedirectForH5Migration(routeName)) {
    return {externalUrl: getH5MigrationExternalGuideUrl()};
  }

  return {
    routeName,
    params,
  };
}
